import React, { useState } from 'react';
import { Avatar, message } from 'antd';
import { useRouter } from 'next/router';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { CheckCircleFilled } from '@ant-design/icons';

import DpModal from '@/components/DpModal';
import { InsMonitorModel, UnAddAccountModel } from '@/types/home';
import { addInsMonitor } from '@/lib/service';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { useTranslation } from 'next-i18next';

interface AddAccountModalProps {
  targetItem: InsMonitorModel | null | undefined;
  unAddAccountList: UnAddAccountModel[];
  clearTargetItem: () => void;
  onAddAccountSuccess: (res: any) => void;
}

const AddAccountModal: React.FC<AddAccountModalProps> = ({
  targetItem,
  unAddAccountList,
  clearTargetItem,
  onAddAccountSuccess,
}) => {
  const router = useRouter();
  const [unAddAccountItem, setUnAddAccountItem] = useState<UnAddAccountModel | null>(null);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleOk = async () => {
    if (!targetItem || !unAddAccountItem) return;
    try {
      setConfirmLoading(true);
      const res = await addInsMonitor(
        targetItem.media_name,
        targetItem.avatar,
        targetItem.media_url,
        unAddAccountItem?.subs_id,
        router.locale
      );
      ga_logEvent(EventName.Add_Account_Request_Success);
      if (res.code !== 0) {
        throw res;
      }
      onAddAccountSuccess(res);
      message.success(res.message);
      setConfirmLoading(false);
      setUnAddAccountItem(null);
      clearTargetItem();
    } catch (error: any) {
      message.error(error?.message || t('Request failed'));
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <DpModal
      open={!!targetItem}
      onCancel={(e) => {
        const isFooterBtn = (e.target as HTMLElement).closest('.next-antd-btn');
        if (isFooterBtn) {
          router.push(
            `/plan?media_name=${targetItem?.media_name}&avatar=${targetItem?.avatar}&media_url=${targetItem?.media_url}`
          );
          setCancelLoading(true);
        } else {
          setUnAddAccountItem(null);
          clearTargetItem();
        }
      }}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      cancelButtonProps={{ loading: cancelLoading }}
      okButtonProps={{ disabled: !unAddAccountItem }}
      destroyOnHidden
      title={t('Add Account')}
      cancelText={t('Create New Subscription')}
      okText={t('Confirm')}
      style={{ maxWidth: '80%' }}
    >
      <div className="flex items-center justify-start">
        <Avatar src={targetItem?.avatar} size={42} className="flex-shrink-0" />
        <span className="ml-3 mr-2 text-[18px] font-bold">{targetItem?.media_name}</span>
        <CheckCircleFilled style={{ color: '#38C13D' }} />
      </div>
      <p className="pt-[18px] pb-7 text-[#999]">
        {t('Add the selected account to your tracked list. Choose a slot below or start a new subscription.')}
      </p>
      <div className="text-[#0C0C0C] font-semibold mb-5">{t('Slot Selection')}</div>
      <div className="pb-4">
        {unAddAccountList?.map?.((item, index) => (
          <div
            key={index}
            className={classnames(
              'px-6 py-4 rounded-[6px] mb-4 cursor-pointer',
              { 'border border-[#D9D9D9]': unAddAccountItem?.subs_id !== item.subs_id },
              { 'border-[#725CFF] border-2 text-[#725CFF] bg-[#F8F7FF]': unAddAccountItem?.subs_id === item.subs_id }
            )}
            onClick={() => setUnAddAccountItem(item)}
          >
            <div className="font-bold">{item?.item_name}</div>
            <div className="text-sm pt-1">
              {t('YourNextBillingDateIs')}{' '}
              {item?.sub_begin_time ? dayjs(item.sub_begin_time).format('YYYY-MM-DD HH:mm:ss') : '--'}
            </div>
          </div>
        ))}
      </div>
    </DpModal>
  );
};

export default AddAccountModal;
